import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PageTabs from '../../ui/PageTabs';
import { t } from '../../utils/i18n';
import { useAuth } from '../../context/AuthContext';

export default function Season() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const location = useLocation();

  const basePath = '/season';
  const tabs = [
    { path: basePath, label: 'Overview', description: 'Alpha Planet Foundation 진행 상황 요약' },
    { path: `${basePath}/events`, label: 'Season Events', description: 'Early Bird & 시즌 이벤트 관리' },
    { path: `${basePath}/tiles`, label: 'Tiles & Map', description: '타일 상태와 도시 성장 패턴' },
    { path: `${basePath}/tickets`, label: 'Tickets & Genesis', description: '티켓 및 제네시스 멤버 통계' },
    { path: `${basePath}/fairness`, label: 'Fairness & Windows', description: '무작위 참여 시작·24시간 규칙 모니터링' },
    { path: `${basePath}/abuse`, label: 'Abuse & Retry', description: '악용 패턴 및 재시도 규칙 모니터링' },
  ];

  const activeTab =
    tabs.find((tab) =>
      tab.path === basePath
        ? location.pathname === basePath || location.pathname === `${basePath}/`
        : location.pathname.startsWith(tab.path),
    ) || tabs[0];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-dark-bg">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text">
              {t('season.title') || 'Season & Land Policy'}
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1 max-w-2xl">
              {t('season.description') ||
                '알파 플래닛 Foundation(토지/시즌 정책)을 기반으로 시즌, 타일, 티켓을 운영·모니터링합니다.'}
            </p>
            <p className="text-dark-text-muted text-xs mt-1">
              Alpha Planet Foundation Policy v0.1 (2026-02-10 KST)
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 justify-end">
              <span className="inline-flex items-center rounded-full border border-dark-border px-3 py-1 text-xs text-dark-text-secondary">
                Admin access · 일부 파라미터만 수정 가능
              </span>
            </div>
          )}
        </div>

        <PageTabs label={t('header.nav.season')} active={activeTab.path} items={tabs.map(tab => ({ id: tab.path, to: tab.path, label: tab.label }))} />

        <p className="mt-3 text-xs text-dark-text-secondary">
          {activeTab.description}
        </p>

        <div className="mt-6">
          <Routes>
            <Route index element={<SeasonOverview />} />
            <Route path="events" element={<SeasonEvents isAdmin={isAdmin} />} />
            <Route path="tiles" element={<TilesMap />} />
            <Route path="tickets" element={<TicketsGenesis />} />
            <Route path="fairness" element={<FairnessWindows />} />
            <Route path="abuse" element={<AbuseRetry isAdmin={isAdmin} />} />
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle }) {
  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-4 flex flex-col gap-1">
      <p className="text-xs font-medium text-dark-text-secondary uppercase tracking-wide">
        {title}
      </p>
      <p className="text-xl font-semibold text-dark-text">{value}</p>
      {subtitle && (
        <p className="text-xs text-dark-text-muted">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function SectionCard({ title, children, footer }) {
  return (
    <section className="bg-dark-card border border-dark-border rounded-lg p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-dark-text">{title}</h2>
        {footer}
      </div>
      <div className="text-sm text-dark-text-secondary space-y-2">
        {children}
      </div>
    </section>
  );
}

function SeasonOverview() {
  const mockPlanetStats = {
    totalTiles: 250000,
    acquiredTiles: 18342,
    eventsTotal: 3,
    eventsActive: 1,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="플래닛 상태"
          value="Alpha Planet · Foundation"
          subtitle="기반(토지/시즌) 정책 운영 단계"
        />
        <StatCard
          title="타일 현황"
          value={`${mockPlanetStats.acquiredTiles.toLocaleString()} / ${mockPlanetStats.totalTiles.toLocaleString()}`}
          subtitle="획득 / 전체 타일 수"
        />
        <StatCard
          title="이벤트 수"
          value={`${mockPlanetStats.eventsActive} / ${mockPlanetStats.eventsTotal}`}
          subtitle="진행 중 / 전체 이벤트"
        />
        <StatCard
          title="정책 버전"
          value="v0.1"
          subtitle="2026-02-10 KST · Alpha Planet Foundation"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="정책 요약"
          footer={
            <span className="text-[10px] text-dark-text-muted">
              Won&apos;t Change 섹션 기반
            </span>
          }
        >
          <ul className="list-disc list-inside space-y-1">
            <li>알파 플래닛의 땅은 사용자만 획득할 수 있으며, 플랫폼은 사전 확보하지 않습니다.</li>
            <li>공개는 Genesis Early Bird → 시즌(Season) 두 단계 구조로 진행됩니다.</li>
            <li>시즌 참여는 무작위 참여 시작 시간 + 24시간 윈도우 규칙을 따릅니다.</li>
            <li>새로 열리는 땅은 항상 기존 획득 타일 주변 8방향에서만 확장됩니다.</li>
            <li>1인 1계정을 지향하며, 향후 인증/회수 정책이 도입될 수 있습니다.</li>
          </ul>
        </SectionCard>

        <SectionCard title="이벤트 진행 요약">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-dark-text-secondary">Genesis Early Bird</span>
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                진행 중 (예시)
              </span>
            </div>
            <p className="text-dark-text-muted text-xs">
              총 25,000칸 중 18,342칸이 획득되었고, 전량 소진 시 즉시 종료됩니다.
            </p>
            <div className="flex items-center justify-between text-xs mt-3">
              <span className="text-dark-text-secondary">최근 시즌</span>
              <span className="text-dark-text-secondary">Season 1 · Season 2</span>
            </div>
            <p className="text-dark-text-muted text-xs">
              각 시즌은 시작 시각(UTC) 공지 후 공정 참여 규칙을 적용하며, 전량 소진 시 자동 종료됩니다.
            </p>
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="경보 / 운영 알림"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            예시 데이터 · 백엔드 연동 시 실제 경보 노출
          </span>
        }
      >
        <p className="text-dark-text-secondary text-sm">
          현재 표시할 경보가 없습니다. 시즌 진행 중 재시도 폭증, 특정 구간 집중, 인증 도입/회수 예정 등
          운영 상 주의가 필요한 이벤트가 발생하면 이 영역에 표시됩니다.
        </p>
      </SectionCard>
    </div>
  );
}

function SeasonEvents({ isAdmin }) {
  const events = [
    {
      id: 'evt-earlybird',
      name: 'Genesis Early Bird',
      type: 'EarlyBird',
      status: '진행 중',
      startsAtUtc: '2026-03-10T00:00:00Z',
      soldOutAtUtc: null,
      tilesOpened: 25000,
      tilesAcquired: 18342,
    },
    {
      id: 'evt-season-1',
      name: 'Season 1',
      type: 'Season',
      status: '예정',
      startsAtUtc: '2026-04-01T00:00:00Z',
      soldOutAtUtc: null,
      tilesOpened: 12000,
      tilesAcquired: 0,
    },
    {
      id: 'evt-season-0',
      name: 'Season 0 (Test)',
      type: 'Season',
      status: '종료',
      startsAtUtc: '2026-02-01T00:00:00Z',
      soldOutAtUtc: '2026-02-03T05:12:34Z',
      tilesOpened: 8000,
      tilesAcquired: 8000,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-dark-card border border-dark-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
          <h2 className="text-sm font-semibold text-dark-text">이벤트 목록</h2>
          <div className="flex items-center gap-2">
            <select className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1">
              <option>모든 유형</option>
              <option>Early Bird</option>
              <option>Season</option>
            </select>
            <select className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1">
              <option>모든 상태</option>
              <option>예정</option>
              <option>진행 중</option>
              <option>종료</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border text-xs text-dark-text-secondary">
                <th className="text-left py-3 px-4">ID</th>
                <th className="text-left py-3 px-4">이벤트명</th>
                <th className="text-left py-3 px-4">유형</th>
                <th className="text-left py-3 px-4">상태</th>
                <th className="text-left py-3 px-4">시작(UTC)</th>
                <th className="text-left py-3 px-4">전량 소진(UTC)</th>
                <th className="text-right py-3 px-4">공개 타일</th>
                <th className="text-right py-3 px-4">획득률</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const acquiredRate =
                  e.tilesOpened === 0 ? 0 : Math.round((e.tilesAcquired / e.tilesOpened) * 100);
                return (
                  <tr
                    key={e.id}
                    className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50"
                  >
                    <td className="py-3 px-4 text-dark-text-secondary text-xs">{e.id}</td>
                    <td className="py-3 px-4 text-dark-text">{e.name}</td>
                    <td className="py-3 px-4 text-dark-text-secondary text-xs">
                      {e.type === 'EarlyBird' ? 'Early Bird' : 'Season'}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      <span
                        className={[
                          'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
                          e.status === '진행 중'
                            ? 'bg-primary/10 text-primary'
                            : e.status === '예정'
                              ? 'bg-dark-surface text-dark-text-secondary'
                              : 'bg-emerald-500/10 text-emerald-300',
                        ].join(' ')}
                      >
                        {e.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-dark-text-secondary text-xs">{e.startsAtUtc}</td>
                    <td className="py-3 px-4 text-dark-text-secondary text-xs">
                      {e.soldOutAtUtc || '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-dark-text-secondary text-xs">
                      {e.tilesOpened.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right text-dark-text-secondary text-xs">
                      {acquiredRate}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <SectionCard
        title="정책 연동 정보"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            6.2, 6.4 섹션 기반 · 읽기 전용 요약
          </span>
        }
      >
        <ul className="list-disc list-inside space-y-1">
          <li>
            각 이벤트는 Genesis Early Bird 1회, 시즌별 1회로 정의되며, 이벤트당 1계정 1장 티켓 사용 제한이
            적용됩니다.
          </li>
          <li>시즌은 시작 시각(UTC) 공지 후 공정 참여 규칙을 적용하며, 전량 소진 시 즉시 종료됩니다.</li>
          <li>
            Early Bird 기간에 사용하지 못한 티켓은 시즌으로 자동 이월되며, 시즌의 공개 영역 안에서만 사용할 수
            있습니다.
          </li>
        </ul>
        {isAdmin && (
          <p className="mt-3 text-[11px] text-dark-text-muted">
            시즌 주기/수량, 다음 시즌 시작 시각 등 변경 가능한 파라미터는 향후 별도 설정 패널에서 수정하도록
            확장할 수 있습니다.
          </p>
        )}
      </SectionCard>
    </div>
  );
}

function TilesMap() {
  const mockGridSize = 8;
  const acquiredTiles = new Set(['2-3', '2-4', '3-3', '3-4', '4-4', '5-5']);
  const newlyOpenedTiles = new Set(['4-5', '5-4', '5-6']);

  const renderTile = (row, col) => {
    const key = `${row}-${col}`;
    const isAcquired = acquiredTiles.has(key);
    const isNew = newlyOpenedTiles.has(key);
    let classes =
      'w-6 h-6 sm:w-7 sm:h-7 border border-dark-border rounded-[3px] flex items-center justify-center text-[9px]';

    if (isAcquired) {
      classes += ' bg-primary/20 text-primary';
    } else if (isNew) {
      classes += ' bg-dark-surface text-dark-text-secondary border-primary/60';
    } else {
      classes += ' bg-black/20 text-dark-text-muted';
    }

    return (
      <div key={key} className={classes}>
        {row},{col}
      </div>
    );
  };

  const rows = Array.from({ length: mockGridSize }, (_, i) => i + 1);
  const cols = Array.from({ length: mockGridSize }, (_, i) => i + 1);

  return (
    <div className="space-y-6">
      <SectionCard
        title="타일 맵 (예시 데이터)"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            실제 서비스에서는 타일 좌표·소유자 데이터와 연동
          </span>
        }
      >
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="inline-block bg-black/40 rounded-lg p-3 border border-dark-border">
              <div className="grid grid-cols-8 gap-1">
                {rows.map((r) => cols.map((c) => renderTile(r, c)))}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 text-xs text-dark-text-secondary">
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-primary/40 border border-primary/60" />
                <span>획득 타일</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-dark-surface border border-primary/60" />
                <span>이번 시즌에 새로 열린 타일</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-black/30 border border-dark-border" />
                <span>미획득 타일</span>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-3 text-xs text-dark-text-secondary">
            <p>
              시즌에서 새로 열리는 땅은 이미 누군가가 획득한 타일의 주변 8방향(가로/세로/대각선)에서만
              확장됩니다. 위 예시에서는 중앙 클러스터 주변에서만 새 타일이 열린 모습을 단순화하여 표현했습니다.
            </p>
            <p>
              실제 관리자 화면에서는 타일 ID, 좌표(x,y), 소유자(마스킹된 ID), 획득 이벤트, 획득 시각 등 메타
              정보와 함께 필터링/검색 기능을 제공합니다.
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="규칙 검증 요약"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            6.5 확장 규칙(8방향) 검증 결과
          </span>
        }
      >
        <ul className="list-disc list-inside space-y-1">
          <li>이번 시즌에서 새로 열린 타일 수: 3 (예시)</li>
          <li>모든 새 타일이 기존 획득 타일의 8방향 내에서만 열렸는지 검증: 위반 없음</li>
          <li>규칙 위반 타일이 존재하는 경우, 좌표·이벤트 ID를 명시하여 경고로 노출합니다.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function TicketsGenesis() {
  const stats = {
    totalUsers: 42037,
    genesisMembers: 25000,
    totalTickets: 42037 + 25000 * 3, // 가입 1장 + 제네시스 추가 3장
    usedTickets: 38912,
    remainingTickets: 42037 + 25000 * 3 - 38912,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="총 가입 사용자 수"
          value={stats.totalUsers.toLocaleString()}
          subtitle="회원가입 완료 기준"
        />
        <StatCard
          title="제네시스 멤버 수"
          value={stats.genesisMembers.toLocaleString()}
          subtitle="앱 출시 이후 25,000번째 가입까지"
        />
        <StatCard
          title="발행된 티켓 수"
          value={stats.totalTickets.toLocaleString()}
          subtitle="가입 기본 1장 + 제네시스 추가 3장"
        />
        <StatCard
          title="사용 / 남은 티켓"
          value={`${stats.usedTickets.toLocaleString()} / ${stats.remainingTickets.toLocaleString()}`}
          subtitle="사용 완료 / 아직 사용되지 않음"
        />
      </div>

      <SectionCard
        title="제네시스 멤버 & 티켓 구조"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            6.3 섹션 기반 · 읽기 전용 설명
          </span>
        }
      >
        <ul className="list-disc list-inside space-y-1">
          <li>회원가입이 완료되면 모든 사용자에게 기본 티켓 1장이 발행됩니다.</li>
          <li>
            제네시스 멤버(최초 25,000명)는 총 4장의 티켓을 받으며, 이 중 1장은 Early Bird 기간에 사용 가능합니다.
          </li>
          <li>나머지 3장은 Early Bird 종료 이후 시즌으로 이월되며, 시즌마다 1장씩만 사용할 수 있습니다.</li>
          <li>향후 정책에 따라 추가 티켓 발행, 타일 이전/공유 기능 등이 도입될 수 있습니다.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function FairnessWindows() {
  const seasonOptions = [
    { id: 'evt-season-1', name: 'Season 1' },
    { id: 'evt-season-0', name: 'Season 0 (Test)' },
  ];

  const distribution = [
    { hourRange: '00:00–04:00', count: 320 },
    { hourRange: '04:00–08:00', count: 295 },
    { hourRange: '08:00–12:00', count: 310 },
    { hourRange: '12:00–16:00', count: 305 },
    { hourRange: '16:00–20:00', count: 298 },
    { hourRange: '20:00–24:00', count: 312 },
  ];

  const total = distribution.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-6">
      <SectionCard title="시즌 선택">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <select className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1 w-full sm:w-60">
            {seasonOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-dark-text-muted">
            선택한 시즌에 대해 무작위 참여 시작 시간 분포와 24시간 윈도우 준수 지표를 확인합니다.
          </p>
        </div>
      </SectionCard>

      <SectionCard
        title="무작위 참여 시작 시간 분포 (예시 데이터)"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            실제 서비스에서는 차트 라이브러리로 시각화
          </span>
        }
      >
        <div className="space-y-3">
          {distribution.map((d) => {
            const ratio = total === 0 ? 0 : Math.round((d.count / total) * 100);
            return (
              <div key={d.hourRange} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-dark-text-secondary">{d.hourRange}</span>
                  <span className="text-dark-text-muted">
                    {d.count.toLocaleString()}명 · {ratio}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-black/40 overflow-hidden">
                  <div
                    className="h-full bg-primary/70"
                    style={{ width: `${Math.max(ratio, 5)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="24시간 윈도우 준수 지표 (예시)">
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>배정된 참여 시작 시간 이후 24시간 안에 티켓을 사용한 사용자 비율: 약 87%</li>
          <li>24시간을 넘겨 사용하지 못해 기한 만료된 티켓 수: 1,283장</li>
          <li>특정 시간대(예: 02:00–04:00)에 과도한 몰림 현상은 관측되지 않았습니다.</li>
        </ul>
      </SectionCard>

      <SectionCard title="개별 검증 툴 (예시)">
        <div className="space-y-3 text-xs">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="text"
              placeholder="사용자 ID 입력"
              className="bg-dark-surface border border-dark-border rounded-md px-3 py-1.5 text-xs text-dark-text placeholder-dark-text-muted flex-1"
            />
            <button className="px-3 py-1.5 rounded-md bg-primary text-black font-medium text-xs hover:bg-highlight transition-colors">
              조회 (모형)
            </button>
          </div>
          <p className="text-dark-text-muted">
            실제 서비스에서는 입력한 사용자 ID에 대해 배정된 참여 시작 시간, 24시간 마감 시각, 실제 사용 시각 및
            결과(성공/실패)를 백엔드에서 조회해 표시합니다.
          </p>
        </div>
      </SectionCard>
    </div>
  );
}

function AbuseRetry({ isAdmin }) {
  const retryIntervalMinutes = 10;

  const attemptsStats = [
    { label: '지난 1시간', attempts: 324, failures: 52 },
    { label: '지난 24시간', attempts: 7250, failures: 912 },
    { label: '지난 7일', attempts: 48120, failures: 6390 },
  ];

  const suspiciousPatterns = [
    { id: 'grp-1', type: '다계정 의심', accounts: 12, reason: '동일 IP, 유사 이메일 패턴' },
    { id: 'grp-2', type: '과도한 재시도', accounts: 3, reason: '10분 간격 최대치에 근접한 재시도' },
  ];

  return (
    <div className="space-y-6">
      <SectionCard
        title="재시도 규칙"
        footer={
          <span className="text-[10px] text-dark-text-muted">
            6.6 섹션 기반 · {retryIntervalMinutes}분 예시
          </span>
        }
      >
        <p className="text-xs text-dark-text-secondary">
          현재 재시도 간격은 획득 시도가 실패한 경우 {retryIntervalMinutes}분 후 재시도할 수 있도록 설정되어
          있습니다. 트래픽/악용 패턴에 따라 값은 조정될 수 있으며, 변경 시 공지됩니다.
        </p>
        {isAdmin && (
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center text-xs">
            <input
              type="number"
              min={1}
              defaultValue={retryIntervalMinutes}
              className="bg-dark-surface border border-dark-border rounded-md px-3 py-1.5 text-xs text-dark-text w-24"
            />
            <select className="bg-dark-surface border border-dark-border rounded-md px-2 py-1.5 text-xs text-dark-text-secondary w-40">
              <option>다음 시즌부터 적용</option>
              <option>즉시 적용</option>
            </select>
            <input
              type="text"
              placeholder="변경 사유 (로그에 기록)"
              className="flex-1 bg-dark-surface border border-dark-border rounded-md px-3 py-1.5 text-xs text-dark-text placeholder-dark-text-muted"
            />
            <button className="px-3 py-1.5 rounded-md bg-primary text-black font-medium text-xs hover:bg-highlight transition-colors">
              저장 (모형)
            </button>
          </div>
        )}
      </SectionCard>

      <SectionCard title="획득 시도 & 실패 통계 (예시)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          {attemptsStats.map((s) => (
            <div
              key={s.label}
              className="bg-black/30 border border-dark-border rounded-md p-3 flex flex-col gap-1"
            >
              <p className="text-dark-text-secondary">{s.label}</p>
              <p className="text-dark-text text-sm">
                시도 {s.attempts.toLocaleString()}건 · 실패 {s.failures.toLocaleString()}건
              </p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="악용 패턴 탐지 요약 (예시)">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-dark-border text-dark-text-secondary">
                <th className="text-left py-2 px-3">ID</th>
                <th className="text-left py-2 px-3">유형</th>
                <th className="text-right py-2 px-3">계정 수</th>
                <th className="text-left py-2 px-3">요약 사유</th>
              </tr>
            </thead>
            <tbody>
              {suspiciousPatterns.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50"
                >
                  <td className="py-2 px-3 text-dark-text-secondary">{p.id}</td>
                  <td className="py-2 px-3 text-dark-text">{p.type}</td>
                  <td className="py-2 px-3 text-right text-dark-text-secondary">
                    {p.accounts.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-dark-text-secondary">{p.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="계정 레벨 상세 (모형)">
        <p className="text-xs text-dark-text-muted">
          실제 서비스에서는 특정 계정을 선택하여 획득 시도 타임라인(성공/실패/대기 상태), IP/디바이스 정보(마스킹),
          인증 상태 등을 상세히 조회할 수 있습니다.
        </p>
      </SectionCard>
    </div>
  );
}

